import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Award, Flame, Zap, Trophy, TrendingUp, Star, BookOpen, Crown, CheckCircle, FileText, Mountain, Clipboard, Target } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MOOD_OPTIONS = [
  { value: 'great', emoji: '\u{1F60A}', label: 'Great', color: 'bg-green-100 border-green-300' },
  { value: 'good', emoji: '\u{1F642}', label: 'Good', color: 'bg-emerald-100 border-emerald-300' },
  { value: 'okay', emoji: '\u{1F610}', label: 'Okay', color: 'bg-yellow-100 border-yellow-300' },
  { value: 'low', emoji: '\u{1F614}', label: 'Low', color: 'bg-orange-100 border-orange-300' },
  { value: 'stressed', emoji: '\u{1F630}', label: 'Stressed', color: 'bg-red-100 border-red-300' },
];

const BADGE_ICONS = {
  award: Award, star: Star, flame: Flame, zap: Zap, trophy: Trophy,
  'trending-up': TrendingUp, 'book-open': BookOpen, 'check-circle': CheckCircle,
  crown: Crown, clipboard: Clipboard, 'file-text': FileText, mountain: Mountain,
};

const GamificationPanel = () => {
  const [profile, setProfile] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [showBadges, setShowBadges] = useState(false);
  const [showMoodCheckin, setShowMoodCheckin] = useState(false);
  const [selectedMood, setSelectedMood] = useState(null);
  const [energyLevel, setEnergyLevel] = useState(3);
  const [journal, setJournal] = useState('');
  const [submittingMood, setSubmittingMood] = useState(false);

  const fetchProfile = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/gamification/profile`);
      setProfile(res.data);
    } catch (err) { /* silent */ }
  }, []);

  useEffect(() => {
    fetchProfile();
    // Auto-refresh every 30 seconds to reflect latest XP/badges
    const interval = setInterval(fetchProfile, 30000);
    return () => clearInterval(interval);
  }, [fetchProfile]);

  const fetchLeaderboard = async () => {
    try {
      const res = await axios.get(`${API}/gamification/leaderboard`);
      setLeaderboard(res.data.leaderboard);
      setShowLeaderboard(true);
    } catch (err) { toast.error('Failed to load leaderboard'); }
  };

  const handleMoodSubmit = async () => {
    if (!selectedMood) { toast.error('Please select a mood'); return; }
    setSubmittingMood(true);
    try {
      await axios.post(`${API}/student/mood-checkin/detailed?mood=${selectedMood}&energy_level=${energyLevel}&journal=${encodeURIComponent(journal)}`);
      toast.success('+10 XP for daily check-in!');
      setShowMoodCheckin(false);
      setSelectedMood(null);
      setJournal('');
      fetchProfile();
    } catch (err) { toast.error('Check-in failed'); }
    finally { setSubmittingMood(false); }
  };

  if (!profile) return null;

  const xpProgress = profile.next_level
    ? ((profile.xp - profile.level.min_xp) / (profile.next_level.min_xp - profile.level.min_xp)) * 100
    : 100;

  return (
    <div className="space-y-4" data-testid="gamification-panel">
      {/* XP & Level Card */}
      <Card className="p-4 bg-gradient-to-r from-indigo-50 to-purple-50 border-indigo-200">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-full bg-indigo-600 text-white flex items-center justify-center font-bold text-lg" data-testid="level-badge">
              {profile.level.level}
            </div>
            <div>
              <p className="font-bold text-indigo-900">{profile.level.name}</p>
              <p className="text-xs text-indigo-600">{profile.xp.toLocaleString()} XP</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {profile.streak_days > 0 && (
              <Badge className="bg-orange-500 text-white flex items-center gap-1" data-testid="streak-badge">
                <Flame className="w-3 h-3" /> {profile.streak_days} day streak
              </Badge>
            )}
          </div>
        </div>
        <div className="space-y-1">
          <div className="flex justify-between text-xs text-indigo-600">
            <span>Level {profile.level.level}</span>
            <span>{profile.next_level ? `${profile.xp_for_next_level} XP to Level ${profile.next_level.level}` : 'MAX LEVEL'}</span>
          </div>
          <Progress value={xpProgress} className="h-2" />
        </div>
      </Card>

      {/* Quick Actions */}
      <div className="grid grid-cols-3 gap-2">
        <Button variant="outline" size="sm" className="h-auto py-2 flex-col gap-1" onClick={() => setShowMoodCheckin(true)} data-testid="mood-checkin-btn">
          <Target className="w-4 h-4 text-green-600" />
          <span className="text-xs">Check In</span>
        </Button>
        <Button variant="outline" size="sm" className="h-auto py-2 flex-col gap-1" onClick={() => setShowBadges(true)} data-testid="badges-btn">
          <Award className="w-4 h-4 text-amber-600" />
          <span className="text-xs">{profile.badges.length} Badges</span>
        </Button>
        <Button variant="outline" size="sm" className="h-auto py-2 flex-col gap-1" onClick={fetchLeaderboard} data-testid="leaderboard-btn">
          <Trophy className="w-4 h-4 text-purple-600" />
          <span className="text-xs">Ranks</span>
        </Button>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-3 gap-2 text-center">
        <div className="p-2 bg-blue-50 rounded-lg">
          <p className="text-lg font-bold text-blue-700">{profile.total_quizzes}</p>
          <p className="text-xs text-blue-500">Quizzes</p>
        </div>
        <div className="p-2 bg-green-50 rounded-lg">
          <p className="text-lg font-bold text-green-700">{profile.total_assignments}</p>
          <p className="text-xs text-green-500">Assignments</p>
        </div>
        <div className="p-2 bg-amber-50 rounded-lg">
          <p className="text-lg font-bold text-amber-700">{profile.perfect_scores}</p>
          <p className="text-xs text-amber-500">Perfect</p>
        </div>
      </div>

      {/* Mood Check-in Dialog */}
      <Dialog open={showMoodCheckin} onOpenChange={setShowMoodCheckin}>
        <DialogContent>
          <DialogHeader><DialogTitle>How are you feeling today?</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="flex justify-center gap-3">
              {MOOD_OPTIONS.map(m => (
                <button key={m.value} onClick={() => setSelectedMood(m.value)}
                  className={`flex flex-col items-center gap-1 p-3 rounded-xl border-2 transition-all ${selectedMood === m.value ? m.color + ' scale-110 shadow-md' : 'border-gray-200 hover:border-gray-300'}`}
                  data-testid={`mood-${m.value}`}>
                  <span className="text-2xl">{m.emoji}</span>
                  <span className="text-xs font-medium">{m.label}</span>
                </button>
              ))}
            </div>
            <div>
              <label className="text-sm font-medium">Energy Level</label>
              <div className="flex gap-2 mt-1">
                {[1,2,3,4,5].map(v => (
                  <button key={v} onClick={() => setEnergyLevel(v)}
                    className={`w-10 h-10 rounded-full border-2 font-bold text-sm transition-all ${energyLevel >= v ? 'bg-yellow-400 border-yellow-500 text-yellow-900' : 'border-gray-200 text-gray-400'}`}>
                    {v}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-sm font-medium">Journal (optional)</label>
              <Textarea value={journal} onChange={e => setJournal(e.target.value)} placeholder="How was your day?" rows={3} className="mt-1" />
            </div>
            <Button className="w-full" onClick={handleMoodSubmit} disabled={submittingMood} data-testid="submit-mood-btn">
              {submittingMood ? 'Saving...' : 'Save & Earn 10 XP'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Badges Dialog */}
      <Dialog open={showBadges} onOpenChange={setShowBadges}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Badges & Achievements</DialogTitle></DialogHeader>
          <div className="grid grid-cols-3 gap-3 py-2">
            {(profile.all_badges || []).map(b => {
              const Icon = BADGE_ICONS[b.icon] || Award;
              return (
                <div key={b.id} className={`p-3 rounded-lg text-center border-2 transition-all ${b.earned ? 'bg-amber-50 border-amber-300' : 'bg-gray-50 border-gray-200 opacity-50'}`} data-testid={`badge-${b.id}`}>
                  <Icon className={`w-6 h-6 mx-auto ${b.earned ? 'text-amber-600' : 'text-gray-400'}`} />
                  <p className="text-xs font-semibold mt-1">{b.name}</p>
                  <p className="text-xs text-gray-500">{b.desc}</p>
                </div>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>

      {/* Leaderboard Dialog */}
      <Dialog open={showLeaderboard} onOpenChange={setShowLeaderboard}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Leaderboard</DialogTitle></DialogHeader>
          <div className="space-y-2 py-2 max-h-[400px] overflow-y-auto">
            {leaderboard.map(entry => (
              <div key={entry.user_id} className={`flex items-center gap-3 p-3 rounded-lg ${entry.is_current_user ? 'bg-indigo-50 border border-indigo-300' : 'bg-gray-50'}`} data-testid={`leaderboard-${entry.rank}`}>
                <span className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${entry.rank <= 3 ? 'bg-amber-400 text-white' : 'bg-gray-200 text-gray-600'}`}>
                  {entry.rank}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm truncate">{entry.name} {entry.is_current_user ? '(You)' : ''}</p>
                  <p className="text-xs text-gray-500">Lv.{entry.level.level} {entry.level.name}</p>
                </div>
                <div className="text-right">
                  <p className="font-bold text-sm text-indigo-700">{entry.xp.toLocaleString()} XP</p>
                  <div className="flex items-center gap-1 text-xs text-gray-400">
                    {entry.streak_days > 0 && <><Flame className="w-3 h-3 text-orange-400" />{entry.streak_days}d</>}
                    <Award className="w-3 h-3 text-amber-400 ml-1" />{entry.badges_count}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GamificationPanel;
